import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@klareospace/pharos-lib-web-utils';
import type { OrderType } from '@klareospace/pharos-lib-types';

import type { ShipperOption, ShipperOptionsStatus } from '../../hooks/use-shipper-options';
import type { OrderCreationFormInput } from '../../schemas/order-creation.schema';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GeneralInformationSectionProps {
  /** Fetched once by `useOrderCreationForm` and passed down — avoids a second `useOrderTypes` call. */
  orderTypes: OrderType[];
  /** Active shipper configurations from `useShipperOptions`, passed down to avoid a second query. */
  shipperOptions: ShipperOption[];
  shipperStatus: ShipperOptionsStatus;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Explanatory message replacing the Shipper select while no option can be resolved — `ready`/`loading` show the select instead. */
const SHIPPER_STATUS_MESSAGE_KEYS: Partial<Record<ShipperOptionsStatus, string>> = {
  error: 'sections.generalInformation.fields.shipper.error',
  forbidden: 'sections.generalInformation.fields.shipper.forbidden',
  none: 'sections.generalInformation.fields.shipper.none',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * "General information" section body — order reference (optional, auto-generated), order type
 * (required), and the Shipper field: a single active shipper configuration auto-selects and locks
 * the select; several require an explicit pick; none/forbidden/error replace it with an explanatory
 * message instead (submission stays blocked — see `useSubmitOrder`'s `shipperOptionsStatus` gate).
 */
export function GeneralInformationSection({
  orderTypes,
  shipperOptions,
  shipperStatus,
}: GeneralInformationSectionProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('order-creation');
  const { control } = useFormContext<OrderCreationFormInput>();

  const shipperMessageKey = SHIPPER_STATUS_MESSAGE_KEYS[shipperStatus];
  const isShipperSelectDisabled = shipperStatus === 'loading' || shipperOptions.length === 1;

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="generalInformation.referenceNumber"
        render={({ field }): React.JSX.Element => (
          <FormItem>
            <FormLabel>{t('sections.generalInformation.fields.reference.label')}</FormLabel>
            <FormControl>
              <Input {...field} placeholder={t('sections.generalInformation.fields.reference.placeholder')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="generalInformation.orderTypeId"
        render={({ field }): React.JSX.Element => (
          <FormItem>
            <FormLabel>
              {tCommon('fields.orderType')} <span aria-hidden="true">*</span>
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {orderTypes.map((orderType) => (
                  <SelectItem key={orderType.id} value={orderType.id}>
                    {orderType.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="generalInformation.shipperCode"
        render={({ field }): React.JSX.Element => {
          if (shipperMessageKey) {
            return (
              <FormItem>
                <FormLabel>{t('sections.generalInformation.fields.shipper.label')}</FormLabel>
                <p className="text-sm text-muted-foreground">{t(shipperMessageKey)}</p>
              </FormItem>
            );
          }
          return (
            <FormItem>
              <FormLabel>
                {t('sections.generalInformation.fields.shipper.label')} <span aria-hidden="true">*</span>
              </FormLabel>
              <Select disabled={isShipperSelectDisabled} onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('sections.generalInformation.fields.shipper.placeholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {shipperOptions.map((option) => (
                    <SelectItem key={option.code} value={option.code}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </div>
  );
}
